import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useOrgBoards } from '../../../hooks/useOrgIssues.js';
import { useMovePilabRequestToIssue } from '../../../hooks/usePilabRequests.js';
import type { ImprovementRequest } from '../../../lib/api/pilab-requests.js';

/**
 * 改善要望をチーム課題に移すポップアップ。移す先のボード（自分が見られるもの）を選ぶ。
 *
 * 課題の中身（タイトル・本文）は API が要望から組み立てるので、ここでは選ばせない。
 * 移せるか（権限・要望の状態・ボードを見られるか）の判定も API にあり、弾かれたら
 * API の文言をそのまま出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 改善要望を移す先の組織課題ボードを選び、要望をチーム課題に移す
 * @graph-connects useOrgBoards [calls] 移す先の候補（見られるボード）
 * @graph-connects useMovePilabRequestToIssue [calls] チーム課題への移動
 */
export function MoveToIssueModal({
  request,
  open,
  onClose,
}: {
  request: ImprovementRequest;
  open: boolean;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const boards = useOrgBoards();
  const move = useMovePilabRequestToIssue();
  const [boardId, setBoardId] = useState('');
  const options = (boards.data?.boards ?? []).map(({ board }) => ({
    value: board.id,
    label: board.name,
  }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('improvements.move.heading')}
      description={t('improvements.move.description')}
    >
      <div className="space-y-3">
        <Select
          label={t('improvements.move.board')}
          value={boardId}
          options={[{ value: '', label: t('improvements.move.boardPlaceholder') }, ...options]}
          onChange={(e) => {
            setBoardId(e.target.value);
          }}
          fullWidth
        />
        {boards.isError && (
          <p className="text-sm text-[var(--color-error)]">{t('improvements.move.boardsError')}</p>
        )}
        {move.isError && (
          <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
            {move.error.message}
          </p>
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            disabled={boardId === ''}
            loading={move.isPending}
            onClick={() => {
              move.mutate({ id: request.id, boardId }, { onSuccess: onClose });
            }}
          >
            {t('improvements.move.submit')}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            {t('improvements.move.cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
